import { useState, useEffect } from "react";
import fetchWithCredentials, { FetchWithCredentialsError } from "apiClient";
import Message from "types/Message";
import { ensureMessageListKey } from "utils/message";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function useManagerChat(page: number): {
  messages: Message[];
  loading: boolean;
  error: string | null;
  next: string | null;
  sendMessage: (
    content: string,
    fileUrl?: string | null
  ) => Promise<Message[] | null>;
} {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [next, setNext] = useState<string | null>(null);

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        const data: PaginatedResponse<Message> = await fetchWithCredentials(
          `/manager-api/manager/chat/?page=${page}`
        );
        setMessages(data.results.map(ensureMessageListKey));
        setNext(data.next);
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Error fetching manager instructions");
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [page]);

  const sendMessage = async (
    content: string,
    fileUrl?: string | null
  ): Promise<Message[] | null> => {
    const body: Record<string, unknown> = { content };
    if (fileUrl) body.fileUrl = fileUrl;
    setError(null);
    try {
      await fetchWithCredentials("/manager-api/manager/chat/", {
        method: "POST",
        body: JSON.stringify(body),
      });
      const data: PaginatedResponse<Message> = await fetchWithCredentials(
        "/manager-api/manager/chat/?page=1"
      );
      const fresh = data.results.map(ensureMessageListKey);
      if (page === 1) {
        setMessages(fresh);
        setNext(data.next);
      }
      return fresh;
    } catch (err: any) {
      const errorMessage = err.message || "Error sending instruction";
      const statusCode =
        err instanceof FetchWithCredentialsError ? err.status : null;
      if (statusCode === null || statusCode < 500) {
        setError(errorMessage);
      }
      return null;
    }
  };

  return { messages, loading, error, next, sendMessage };
}
